import styles from "./css/MessagingPage.module.css";
import "bootstrap/dist/css/bootstrap.css";
import { useEffect, useRef, useState } from "react";
import SearchView from "./components/SearchView";
import ConversationCard from "./components/ConversationCard";
import {
  deleteMsgById,
  getChats,
  getMessages,
  postMessage,
} from "../../api/messages/messagesApi";
import {
  editMessage,
  sendMessageViaSocket,
  socketSetup,
} from "../../api/messages/messageSocket";
import { formatImageUrl, groupMessagesByDate } from "../../utils";
import { useMediaQuery } from "@mui/material";
import OptionModal from "../../components/chat/OptionModal";
import EditMessageModal from "./components/EditMessageModal";
import MessageList from "./components/MessageList";
import SearchPlaceholder from "../../components/chat/SearchPlaceholder";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import i18next, { t } from "i18next";
import EmojiPicker from "emoji-picker-react";

import ChatbotPanel from "./components/ChatbotPanel/ChatbotPanel";

function MessagingPage() {
  const [chats, setChats] = useState([]);
  const [chat, setChat] = useState(null);
  const [isBotOpen, setIsBotOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showSendBtn, setShowSendBtn] = useState(false);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showEdit, setShowEdit] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Added state variables for translations
  const [currentLanguage, setCurrentLanguage] = useState(i18next.language);
  const [translations, setTranslations] = useState({
    messageTitle: t("message.title"),
    searchPlaceholder: t("message.searchPlaceholder"),
    messagePlaceholder: t("message.messagePlaceholder"),
    noChatSelected: t("message.noChatSelected"),
  });

  const socketRef = useRef(null);
  const isMobileView = useMediaQuery("(max-width:800px)");
  const isTabletView = useMediaQuery("(max-width:1024px)");
  const emojiPickerRef = useRef(null);

  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const searchViewRef = useRef(null);
  const fileInputRef = useRef(null);

  // Helper: filter out messages that should not be visible
  const filterVisibleMessages = (messages) => {
    if (!Array.isArray(messages)) return [];
    return messages.filter((msg) => !msg.deleted_for_sender);
  };

  // Helper function to check if a chat has messages
  const hasMessages = (chat) => {
    // Ensure dm_info exists
    if (!chat?.dm_info) return false;

    const hasLastMessage =
      chat.dm_info.last_msg &&
      chat.dm_info.last_msg !== "" &&
      chat.dm_info.last_msg !== "No messages yet";

    const hasValidTimestamp =
      chat.dm_info.msg_timestamp && chat.dm_info.msg_timestamp !== null;

    return hasLastMessage && hasValidTimestamp;
  };

  // Helper function to get profile picture from different possible locations
  const getProfilePicture = (chatData) => {
    let profilePicUrl = null;

    if (chatData?.profile_pic) {
      profilePicUrl = chatData.profile_pic;
    } else if (
      chatData?.dm_info?.members?.length > 0 &&
      chatData.dm_info.members[0]?.profile_pic
    ) {
      profilePicUrl = chatData.dm_info.members[0].profile_pic;
    }

    if (profilePicUrl) {
      return formatImageUrl(profilePicUrl, process.env.REACT_APP_API_BASE_URL);
    }

    return null;
  };

  const ProfilePicture = ({ chatData, size = 48, className = "" }) => {
    const [imageError, setImageError] = useState(false);
    const profilePicUrl = getProfilePicture(chatData);

    const handleImageError = () => {
      setImageError(true);
    };

    if (!profilePicUrl || imageError) {
      return (
        <div
          className={`${className} rounded-circle bg-secondary d-flex align-items-center justify-content-center`}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            minWidth: `${size}px`,
          }}
        >
          <PersonIcon
            style={{
              fontSize: `${size * 0.6}px`,
              color: "white",
            }}
          />
        </div>
      );
    }

    return (
      <img
        className={`${className} rounded-circle`}
        src={profilePicUrl}
        alt="profile"
        onError={handleImageError}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          objectFit: "cover",
          minWidth: `${size}px`,
        }}
      />
    );
  };

  useEffect(() => {
    const languageChanged = () => {
      setCurrentLanguage(i18next.language);
      setTranslations({
        messageTitle: t("message.title"),
        searchPlaceholder: t("message.searchPlaceholder"),
        messagePlaceholder: t("message.messagePlaceholder"),
        noChatSelected: t("message.noChatSelected"),
      });
    };

    i18next.on("languageChanged", languageChanged);

    return () => {
      i18next.off("languageChanged", languageChanged);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const filteredSetMessages = (updater) => {
      if (typeof updater === "function") {
        setMessages((prev) => {
          const next = updater(prev);
          return filterVisibleMessages(next);
        });
      } else {
        const visible = filterVisibleMessages(updater);
        setMessages(visible);
      }
    };

    const stableSetChat = (valueOrUpdater) => {
      setChat((prev) => {
        // if prev is null we keep placeholder visible — don't let socket pick chat for you
        if (!prev) return prev;

        const next =
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(prev)
            : valueOrUpdater;

        if (next && next.channel && next.channel === prev.channel) {
          return next;
        }

        return prev;
      });
    };

    socketSetup(
      socketRef,
      setChats,
      filteredSetMessages,
      stableSetChat,
      updateChatList,
      isMobileView
    );

    return () => {
      socketRef.current?.close();
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    updateMsgList();
  }, [chat, isBotOpen]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() && !selectedFile) return;
    if (!chat?.channel) return; // guard

    const formData = new FormData();
    formData.append("channel_id", chat.channel);
    formData.append("message", inputMessage);
    if (selectedFile) formData.append("file_url", selectedFile);
    try {
      const messageId = await postMessage(formData);
      if (messageId)
        sendMessageViaSocket(socketRef.current, messageId, chat.channel);
      setInputMessage("");
      setSelectedFile(null);
      setShowEmojiPicker(false);
      updateChatList();
    } catch (e) {
      console.log("Unable to send message", e);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (e.shiftKey) {
        setInputMessage((prev) => prev + "\n");
      } else {
        e.preventDefault();
        sendMessage();
      }
    }
  };

  const updateChatList = async () => {
    const chats = await getChats();
    setChats(chats);
  };

  const updateMsgList = async () => {
    // If the bot panel is open we don't load messages
    if (isBotOpen) return;
    if (!chat?.channel) return;
    const res = await getMessages(chat.channel);
    const visible = filterVisibleMessages(res?.data || []);
    setMessages(visible);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const groupedMessages = groupMessagesByDate(messages);

  const resetSearch = () => {
    setSearchTerm("");
  };

  const handleMessageMenuClick = async (action) => {
    if (action === "delete") {
      await deleteMsgById(selectedMsg.id);
      updateChatList();
      updateMsgList();
    } else {
      setShowEditModal(true);
    }
    setShowOptionModal(false);
  };

  const handleEdit = (editedMsg) => {
    editMessage(socketRef.current, editedMsg, selectedMsg);
  };

  const onEmojiClick = (emojiData, event) => {
    const data = event || emojiData;

    let emoji = "";

    if (typeof data === "string") {
      emoji = data;
    } else if (data?.emoji) {
      emoji = data.emoji;
    } else if (data?.native) {
      emoji = data.native;
    } else if (data?.unified) {
      emoji = String.fromCodePoint(
        ...data.unified.split("-").map((u) => parseInt(u, 16))
      );
    }

    if (emoji) {
      setInputMessage((prev) => prev + emoji);
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffDays <= 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  // Filter chats that have messages
  const chatsWithMessages = chats.filter(hasMessages);

  // Always include the currently open chat in the sidebar,
  // even if it temporarily has no messages (e.g. after deletion).
  const sidebarChats =
    chat && !chatsWithMessages.some((c) => c.id === chat.id)
      ? [chat, ...chatsWithMessages]
      : chatsWithMessages;

  // NEW: open chatbot without changing `chat` state
  const openChatbot = () => {
    setIsBotOpen(true);
    setChat(null);
  };

  const closeChatbot = () => {
    setIsBotOpen(false);
  };

  return (
    <div
      className={`${styles.main} rounded-3`}
      style={{ height: "100vh", maxHeight: "100vh" }}
    >
      <div className={`d-flex w-100 h-100`}>
        {/* Sidebar */}
        {(!isMobileView || (isMobileView && chat === null && !isBotOpen)) && (
          <div
            className={`${styles["message-list"]} border-end`}
            style={{
              width: isMobileView ? "100%" : isTabletView ? "300px" : "350px",
              minWidth: isMobileView ? "auto" : "280px",
              maxWidth: isMobileView ? "100%" : "400px",
            }}
          >
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between px-3 px-md-4 py-3 border-bottom">
              <h5 className="fw-bold m-0" style={{ color: "#F33070" }}>
                {translations.messageTitle}
              </h5>
            </div>

            {/* Search Box */}
            <div className="p-2 p-md-3 border-bottom">
              <div
                className={`${styles["searchbox-wrapper"]} d-flex align-items-center bg-light rounded-pill px-3 py-2`}
              >
                <SearchIcon className="text-muted me-2" fontSize="small" />
                <input
                  type="text"
                  placeholder={translations.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control-plaintext text-secondary flex-grow-1 border-0 bg-transparent"
                  style={{
                    outline: "none",
                    boxShadow: "none",
                    fontSize: isMobileView ? "14px" : "16px",
                  }}
                />
                {showSendBtn && (
                  <button
                    className="btn btn-sm p-1"
                    onClick={() => {
                      if (searchViewRef.current)
                        searchViewRef.current.handleSendMessage();
                    }}
                  >
                    <SendIcon fontSize="small" className="text-primary" />
                  </button>
                )}
              </div>
            </div>

            <div
              className={`${styles.chatItem} p-2 p-md-3 mb-2 cursor-pointer`}
              onClick={openChatbot}
              role="button"
              aria-label="Open nxNearby AI chatbot"
              style={{
                margin: "12px",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                background: isBotOpen
                  ? "linear-gradient(135deg,#ec4899 0%,#db2777 100%)"
                  : "#fff",
                color: isBotOpen ? "#fff" : undefined,
                border: isBotOpen ? "2px solid #db2777" : "2px solid #f0f0f0",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div>
                <img
                  src="/Image/HomeImages/logo-nxnearby-icon.png"
                  alt="nxNearby AI"
                  style={{
                    width: isMobileView ? 40 : 48,
                    height: isMobileView ? 40 : 48,
                    borderRadius: "50%",
                    objectFit: "cover",
                    minWidth: isMobileView ? 40 : 48,
                    background: "#fff",
                    padding: "5px",
                  }}
                />
              </div>
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <h6
                  className="mb-0 fw-semibold text-truncate"
                  style={{ fontSize: isMobileView ? "14px" : "16px" }}
                >
                  nxNearby AI
                </h6>
                <p
                  className="mb-0 text-muted small text-truncate"
                  style={{ fontSize: isMobileView ? "12px" : "14px" }}
                >
                  Smart profile, job & course search
                </p>
              </div>
            </div>

            {/* Chat List */}
            <div
              className={`${styles["message-list-container"]} flex-grow-1 overflow-y-auto`}
              style={{ maxHeight: "calc(100vh - 240px)" }}
            >
              <SearchView
                searchTerm={searchTerm}
                chats={chats}
                setChat={(c) => {
                  setChat(c);
                  setIsBotOpen(false);
                }}
                setChats={setChats}
                messageInputRef={messageInputRef}
                resetSearch={resetSearch}
                setShowSendBtn={setShowSendBtn}
                ref={searchViewRef}
              />

              {searchTerm === "" ? (
                sidebarChats.length > 0 ? (
                  sidebarChats.map((curChat) => (
                    <div
                      key={curChat.id}
                      onClick={() => {
                        setChat(curChat);
                        setIsBotOpen(false);
                      }}
                      className={`${styles.chatItem} ${
                        curChat?.id === chat?.id ? styles.activeChatItem : ""
                      } p-2 p-md-3 border-bottom cursor-pointer`}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-start">
                        <div className="position-relative me-2 me-md-3">
                          <ProfilePicture
                            chatData={curChat}
                            size={isMobileView ? 40 : 48}
                          />
                          <div
                            className="position-absolute bottom-0 end-0 bg-success rounded-circle"
                            style={{
                              width: isMobileView ? "10px" : "12px",
                              height: isMobileView ? "10px" : "12px",
                              border: "2px solid white",
                            }}
                          ></div>
                        </div>
                        <div className="flex-grow-1 min-width-0">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <h6
                              className="mb-0 fw-semibold text-dark text-truncate"
                              style={{
                                fontSize: isMobileView ? "14px" : "16px",
                              }}
                            >
                              {curChat?.dm_info?.members?.length === 1
                                ? curChat?.dm_info?.members[0]?.dm_name
                                : curChat?.dm_info?.members
                                    ?.map((member) => member.dm_name)
                                    .join(", ")}
                            </h6>
                            <small
                              className="text-muted ms-2 flex-shrink-0"
                              style={{
                                fontSize: isMobileView ? "10px" : "12px",
                              }}
                            >
                              {formatTimestamp(curChat?.dm_info?.msg_timestamp)}
                            </small>
                          </div>
                          <p
                            className="mb-0 text-muted small text-truncate"
                            style={{ fontSize: isMobileView ? "12px" : "14px" }}
                          >
                            {curChat?.dm_info?.last_msg}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4">
                    <SearchPlaceholder />
                  </div>
                )
              ) : (
                <></>
              )}
            </div>
          </div>
        )}

        {/* Chat Area - special render for the chatbot */}
        {isBotOpen ? (
          <div
            className={`${styles["conversations"]} d-flex flex-column flex-grow-1`}
            style={{ minWidth: 0 }}
          >
            <ChatbotPanel
              apiBase={process.env.REACT_APP_AI_API_BASE || ""}
              authToken={
                process.env.REACT_APP_AI_TOKEN ||
                "bdd4b8717f1b87fb2e475f2bcb0ad97c46ebde07"
              }
              onClose={() => closeChatbot()}
            />
          </div>
        ) : chat ? (
          <div
            className={`${styles["conversations"]} d-flex flex-column flex-grow-1`}
            style={{ minWidth: 0 }}
          >
            {/* Chat Header */}
            <div
              className={`${styles.conversationHeader} d-flex gap-2 gap-md-3 px-3 px-md-4 py-3 border-bottom bg-white align-items-center`}
            >
              {isMobileView && (
                <button className="btn p-0" onClick={() => setChat(null)}>
                  <img
                    src="/Image/lecturePlan/back-arrow.svg"
                    alt="back"
                    style={{ width: "1.3rem" }}
                  />
                </button>
              )}
              <div className="position-relative">
                <ProfilePicture chatData={chat} size={isMobileView ? 35 : 40} />
                <div
                  className="position-absolute bottom-0 end-0 bg-success rounded-circle"
                  style={{
                    width: isMobileView ? "8px" : "10px",
                    height: isMobileView ? "8px" : "10px",
                    border: "2px solid white",
                  }}
                ></div>
              </div>
              <div className="d-flex justify-content-center flex-column min-width-0 flex-grow-1">
                <h6
                  className="mb-0 fw-semibold text-truncate"
                  style={{ fontSize: isMobileView ? "14px" : "16px" }}
                >
                  {chat?.dm_info?.members?.length === 1
                    ? chat?.dm_info?.members[0]?.dm_name
                    : chat?.dm_info?.members
                        ?.map((member) => member.dm_name)
                        .join(", ")}
                </h6>
                <small
                  className="text-success"
                  style={{ fontSize: isMobileView ? "11px" : "12px" }}
                >
                  Active now
                </small>
              </div>
            </div>

            {/* Messages Area */}
            <div
              className="flex-grow-1 overflow-y-auto p-2 p-md-3"
              style={{
                maxHeight: "calc(100vh - 200px)",
                minHeight: 0,
              }}
            >
              <MessageList
                groupedMessages={groupedMessages}
                setShowOptionModal={setShowOptionModal}
                setSelectedMsg={setSelectedMsg}
                messagesEndRef={messagesEndRef}
                setShowEdit={setShowEdit}
              />
            </div>

            {/* Message Input Area */}
            <div className="border-top bg-white p-2 p-md-3">
              {/* File Preview */}
              {selectedFile && (
                <div
                  className={`${styles.fileContainer} d-flex align-items-center p-2 mb-2 bg-light rounded`}
                >
                  <div className={`${styles.fileIconContainer} me-2`}>
                    <img
                      src="/Image/Messaging/file.svg"
                      alt="file_uploaded"
                      style={{ width: "20px", height: "20px" }}
                    />
                  </div>
                  <span
                    className="flex-grow-1 text-truncate"
                    style={{ fontSize: isMobileView ? "12px" : "14px" }}
                  >
                    {selectedFile.name}
                  </span>
                  <CloseRoundedIcon
                    className="ms-2 text-muted cursor-pointer"
                    onClick={() => setSelectedFile(null)}
                    style={{ cursor: "pointer" }}
                  />
                </div>
              )}

              {/* Input Box */}
              <div className="d-flex align-items-end gap-2">
                <div className="flex-grow-1">
                  <div
                    className={`${styles["input-wrapper"]} d-flex align-items-end border rounded-pill px-2 px-md-3 py-2 bg-light`}
                  >
                    {/* Attach File */}
                    <button
                      className={`btn p-1 me-1 me-md-2 ${styles.attachBtn}`}
                      onClick={() => fileInputRef.current.click()}
                      type="button"
                    >
                      <AttachFileIcon
                        className="text-muted"
                        fontSize={isMobileView ? "small" : "medium"}
                        style={{ transform: "rotate(45deg)" }}
                      />
                    </button>
                    <input
                      type="file"
                      className="d-none"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                    />

                    {/* Text Input */}
                    <textarea
                      placeholder={translations.messagePlaceholder}
                      className={`${styles.messageInput} form-control border-0 bg-transparent resize-none`}
                      value={inputMessage}
                      onKeyDown={handleKeyPress}
                      onChange={(e) => setInputMessage(e.target.value)}
                      ref={messageInputRef}
                      rows={1}
                      style={{
                        outline: "none",
                        boxShadow: "none",
                        maxHeight: "100px",
                        overflowY: "auto",
                        fontSize: isMobileView ? "14px" : "16px",
                      }}
                    />

                    {/* Emoji Picker */}
                    <div className="position-relative" ref={emojiPickerRef}>
                      <button
                        className="btn pl-1 mx-1 mx-md-2"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        type="button"
                      >
                        <EmojiEmotionsIcon
                          className="text-muted"
                          fontSize={isMobileView ? "small" : "medium"}
                        />
                      </button>
                      {showEmojiPicker && (
                        <div
                          className="position-fixed "
                          style={{
                            zIndex: 9999,
                            bottom: isMobileView ? "70px" : "40%",
                            left: isMobileView ? "50%" : "40%",
                            transform: isMobileView
                              ? "translateX(-50%)"
                              : "none",
                            right: isMobileView ? "auto" : "0",
                            ...(isMobileView
                              ? {}
                              : {
                                  bottom: "50%",
                                  marginBottom: "8px",
                                  transform: "translateX(50%)",
                                  right: "20%",
                                }),
                          }}
                        >
                          <div
                            style={{
                              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                              borderRadius: "12px",
                              overflow: "hidden",
                            }}
                          >
                            <EmojiPicker
                              onEmojiClick={onEmojiClick}
                              width={isMobileView ? 280 : 300}
                              height={isMobileView ? 350 : 400}
                              searchDisabled={false}
                              skinTonesDisabled={false}
                              previewConfig={{
                                showPreview: false,
                              }}
                              theme="light"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Send Button */}
                <button
                  className={`btn rounded-circle d-flex align-items-center justify-content-center ${
                    !inputMessage.trim() && !selectedFile ? "opacity-50" : ""
                  } ${styles.sendBtn}`}
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() && !selectedFile}
                  style={{
                    backgroundColor: "#F33070",
                    borderColor: "#F33070",
                    width: isMobileView ? "35px" : "40px",
                    height: isMobileView ? "35px" : "40px",
                    minWidth: isMobileView ? "35px" : "40px",
                  }}
                >
                  <SendIcon
                    fontSize={isMobileView ? "small" : "small"}
                    sx={{ color: "white" }}
                  />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`${styles.messageWindowPlaceholder} d-flex align-items-center justify-content-center flex-grow-1 text-muted`}
          >
            <div className="text-center p-3">
              <div className="mb-3">
                <SearchIcon
                  style={{ fontSize: isMobileView ? "3rem" : "4rem" }}
                  className="text-muted opacity-50"
                />
              </div>
              <h5
                className="text-muted"
                style={{ fontSize: isMobileView ? "18px" : "20px" }}
              >
                {translations.noChatSelected}
              </h5>
              <p
                className="text-muted"
                style={{ fontSize: isMobileView ? "14px" : "16px" }}
              >
                Select a conversation to start messaging
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <OptionModal
        open={showOptionModal}
        handleClose={() => setShowOptionModal(false)}
        showEdit={showEdit}
        handleListItemClick={handleMessageMenuClick}
      />

      <EditMessageModal
        open={showEditModal}
        handleClose={() => setShowEditModal(false)}
        messageText={selectedMsg?.body}
        handleEdit={handleEdit}
      />
    </div>
  );
}

export default MessagingPage;
